import { useEffect, useMemo } from "react";
import { LayerTypePrintableProps } from "../../types";
import { useGeometryTransformer } from "react-migration/layouts/SiteReport/hooks/useGeometryTransformer";
import { useTransactionsAtSelection } from "./useTransactionsAtSelection";
import { ScopeType } from "src/js/stores/site/store";
import { negativeBuffer } from "react-migration/layouts/map/Multilayer/geometry_utilities";
import {
  EMPTY_IDS,
  useSiteReportConfigurationState,
} from "react-migration/layouts/SiteReport/useSiteReportConfiguration";
import clsx from "clsx";
import { ComparablesPrintableTable } from "./ComparablesPrintableTable";
import { addBufferToGeometry } from "react-migration/domains/sites/helpers/addBufferToGeometry";
import { ComparablesState } from "react-migration/domains/comparables/typings/Comparables";

/** Comparables are identified via their address. */
function comparableIdentifier<T extends { address: string }>(comparable: T) {
  return comparable.address;
}

const noFilters = {} as
  | ComparablesState["hmlrFilters"]
  | ComparablesState["hometrackFilters"]
  | ComparablesState["filters"];

export function ComparablesPrintable({ layer, selection, onLoaded, onUnmount }: LayerTypePrintableProps) {
  const siteId = selection.id as string;
  const { scopeConfig } = useGeometryTransformer(siteId, "COMPARABLES", selection);

  const onSiteSelection = useMemo(() => {
    const baseGeometry = selection.feature?.geometry;
    if (!baseGeometry) return selection;

    const onSiteGeometry = negativeBuffer(baseGeometry) ?? baseGeometry;
    return {
      ...selection,
      feature: {
        type: "Feature" as const,
        geometry: onSiteGeometry,
        properties: {},
      },
    };
  }, [selection]);

  // Create independent geometry for nearby comparables (always uses positive buffer)
  const nearbySelection = useMemo(() => {
    const baseGeometry = selection.feature?.geometry;
    if (!baseGeometry) return null;

    const radius = scopeConfig.radius ?? 100;
    const nearbyGeometry = addBufferToGeometry(baseGeometry, radius)?.geometry ?? baseGeometry;
    return {
      ...selection,
      feature: {
        type: "Feature" as const,
        geometry: nearbyGeometry,
        properties: {},
      },
    };
  }, [selection, scopeConfig.radius]);

  const { includedResults: onSiteComparables, loading } = useTransactionsAtSelection(
    onSiteSelection,
    undefined,
    noFilters
  );

  const { includedResults: allComparables, loading: nearbyLoading } = useTransactionsAtSelection(
    scopeConfig.scopeType === ScopeType.NEARBY && nearbySelection ? nearbySelection : selection,
    undefined,
    noFilters
  );

  // Deduplicate to get nearby-only comparables (excluding on-site)
  const nearbyOnlyComparables = useMemo(() => {
    if (scopeConfig.scopeType !== ScopeType.NEARBY || !onSiteComparables || !allComparables) {
      return [];
    }

    const onSiteAddresses = new Set(onSiteComparables.map(comparableIdentifier));
    return allComparables.filter((comparable) => !onSiteAddresses.has(comparableIdentifier(comparable)));
  }, [scopeConfig.scopeType, onSiteComparables, allComparables]);

  const comparableIds = useMemo(() => onSiteComparables?.map(comparableIdentifier) ?? EMPTY_IDS, [onSiteComparables]);

  const nearbyComparableIds = useMemo(
    () => nearbyOnlyComparables?.map(comparableIdentifier) ?? EMPTY_IDS,
    [nearbyOnlyComparables]
  );

  const { hiddenIds, allHidden, allVisible, toggleId, toggleAll } = useSiteReportConfigurationState(
    "excludeComparableIds",
    selection.id as string,
    comparableIds
  );

  const {
    hiddenIds: nearbyHiddenIds,
    allHidden: nearbyAllHidden,
    allVisible: nearbyAllVisible,
    toggleId: nearbyToggleId,
    toggleAll: nearbyToggleAll,
  } = useSiteReportConfigurationState("excludeComparableIds", `${selection.id}_nearby`, nearbyComparableIds);

  useEffect(() => {
    const isOnSiteLoaded = !loading;
    const isNearbyLoaded = scopeConfig.scopeType !== ScopeType.NEARBY || !nearbyLoading;

    if (isOnSiteLoaded && isNearbyLoaded) {
      onLoaded();
    }
  }, [loading, nearbyLoading, scopeConfig.scopeType, onLoaded]);

  useEffect(() => {
    return () => {
      onUnmount();
    };
  }, [onUnmount]);

  const shouldHideInPrint = scopeConfig.scopeType === ScopeType.NEARBY ? allHidden && nearbyAllHidden : allHidden;

  if (
    (scopeConfig.scopeType === ScopeType.ON_SITE && !onSiteComparables?.length) ||
    (scopeConfig.scopeType === ScopeType.NEARBY && !nearbyOnlyComparables?.length && !onSiteComparables?.length)
  ) {
    return null;
  }

  return (
    <div
      data-testid="comparables-report"
      className={clsx("mb-4 print-landscape", { "print:hidden": shouldHideInPrint })}
    >
      <h2 className="text-base">{layer.title}</h2>

      <ComparablesPrintableTable
        comparables={onSiteComparables}
        hiddenIds={hiddenIds}
        allVisible={allVisible}
        toggleId={toggleId}
        toggleAll={toggleAll}
        comparableIdentifier={comparableIdentifier}
        scopeConfig={scopeConfig}
        scopeType={ScopeType.ON_SITE}
      />

      {scopeConfig.scopeType === ScopeType.NEARBY && nearbyOnlyComparables?.length > 0 && (
        <div className="mt-6">
          <ComparablesPrintableTable
            comparables={nearbyOnlyComparables}
            hiddenIds={nearbyHiddenIds}
            allVisible={nearbyAllVisible}
            toggleId={nearbyToggleId}
            toggleAll={nearbyToggleAll}
            comparableIdentifier={comparableIdentifier}
            scopeConfig={scopeConfig}
            scopeType={ScopeType.NEARBY}
          />
        </div>
      )}
    </div>
  );
}
